import { Box } from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import LanguageIcon from '@mui/icons-material/Language';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import i18n from '../../i18n';
import type { ResolvedZone, ZoneSize } from './geoFlags';

/**
 * Renders a country flag (SVG via flagcdn.com — crisp on every OS, no bundled
 * assets) for a resolved zone/language, or a neutral icon when there's no
 * country (worldwide / continent / unresolved). See geoFlags.ts.
 */
export function Flag({ countryCode, size = 22, title }: { countryCode?: string; size?: number; title?: string }) {
  if (!countryCode) {
    return <NeutralIcon size={size} kind="world" />;
  }
  return (
    <Box
      component="img"
      src={`https://flagcdn.com/${countryCode}.svg`}
      alt={title || countryCode}
      title={title}
      loading="lazy"
      sx={{
        width: size,
        height: Math.round(size * 0.72),
        objectFit: 'cover',
        borderRadius: 0.75,
        boxShadow: 'inset 0 0 0 1px rgba(11,11,15,0.12)',
        flexShrink: 0,
        display: 'block',
      }}
    />
  );
}

function NeutralIcon({ size, kind }: { size: number; kind: 'world' | 'continent' | 'pin' }) {
  const Icon = kind === 'world' ? PublicIcon : kind === 'continent' ? LanguageIcon : LocationOnIcon;
  return (
    <Box sx={{ width: size, height: Math.round(size * 0.72), display: 'grid', placeItems: 'center', flexShrink: 0 }}>
      <Icon sx={{ fontSize: size * 0.85, color: 'text.secondary' }} />
    </Box>
  );
}

/** Flag for a resolved ZONE — flag when there's a country, else a size-aware icon. */
export function ZoneFlag({ zone, size = 22, title }: { zone: ResolvedZone; size?: number; title?: string }) {
  if (zone.countryCode) return <Flag countryCode={zone.countryCode} size={size} title={title} />;
  const kind: 'world' | 'continent' | 'pin' = zone.size === 'world' ? 'world' : zone.size === 'continent' ? 'continent' : 'pin';
  return <NeutralIcon size={size} kind={kind} />;
}

const SIZE_LABEL_KEY: Record<ZoneSize, string> = {
  world: 'flag.size.world',
  continent: 'flag.size.continent',
  country: 'flag.size.country',
  region: 'flag.size.region',
  city: 'flag.size.city',
  village: 'flag.size.village',
};

export function sizeLabel(size: ZoneSize): string {
  return i18n.t(SIZE_LABEL_KEY[size]);
}
